// src/composables/usePrint.js
// Composable สำหรับ print โดยไม่กระทบหน้าเว็บหลัก
// ใช้ iframe ชั่วคราว inject HTML + CSS แล้วเรียก print()

export function usePrint() {
  /**
   * printHtml(htmlContent)
   * รับ HTML string → inject ลง iframe → print → ลบ iframe ทิ้ง
   */
  const printHtml = (htmlContent) => {
    // สร้าง iframe ซ่อน
    const iframe = document.createElement('iframe')
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:0;'
    document.body.appendChild(iframe)

    const doc = iframe.contentWindow.document
    doc.open()
    doc.write(htmlContent)
    doc.close()

    // รอ font/image โหลดก่อน print
    iframe.contentWindow.onload = () => {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
      // ลบ iframe หลัง print dialog ปิด
      setTimeout(() => document.body.removeChild(iframe), 1000)
    }
  }

  // ─── CSS พื้นฐานสำหรับเอกสาร print ──────────────────
  const basePrintCss = `
    @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700&display=swap');

    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Sarabun', sans-serif;
      font-size: 14px;
      color: #1a1a1a;
      background: #fff;
      padding: 0;
    }

    .page {
      width: 210mm;
      min-height: 297mm;
      padding: 18mm 20mm;
      margin: 0 auto;
      background: #fff;
    }

    /* ─── Header ─── */
    .doc-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 14px;
      border-bottom: 2px solid #0d6efd;
      margin-bottom: 20px;
    }

    .doc-logo {
      font-size: 22px;
      font-weight: 700;
      color: #0d6efd;
    }

    .doc-logo small {
      display: block;
      font-size: 12px;
      font-weight: 400;
      color: #6c757d;
      margin-top: 2px;
    }

    .doc-title {
      text-align: right;
    }

    .doc-title h1 {
      font-size: 20px;
      font-weight: 700;
      color: #1a1a1a;
    }

    .doc-title .doc-no {
      font-size: 13px;
      color: #6c757d;
      margin-top: 4px;
    }

    /* ─── Section ─── */
    .section {
      margin-bottom: 20px;
    }

    .section-title {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #6c757d;
      border-bottom: 1px solid #e9ecef;
      padding-bottom: 6px;
      margin-bottom: 12px;
    }

    /* ─── Grid ─── */
    .grid-2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px 24px;
    }

    .grid-3 {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 10px 16px;
    }

    .field-label {
      font-size: 11px;
      color: #6c757d;
      margin-bottom: 3px;
    }

    .field-value {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
    }

    /* ─── Table ─── */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 12px;
    }

    thead th {
      background: #f8f9fa;
      padding: 8px 10px;
      font-size: 12px;
      font-weight: 700;
      color: #495057;
      border-bottom: 2px solid #dee2e6;
      text-align: left;
    }

    tbody td {
      padding: 9px 10px;
      font-size: 13px;
      border-bottom: 1px solid #f0f0f0;
      vertical-align: middle;
    }

    tbody tr:last-child td { border-bottom: none; }

    .text-end { text-align: right; }
    .text-center { text-align: center; }

    /* ─── Summary Box ─── */
    .summary-box {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 14px 18px;
      margin-top: 12px;
    }

    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 5px 0;
      font-size: 13px;
    }

    .summary-row.total {
      font-size: 16px;
      font-weight: 700;
      border-top: 2px solid #dee2e6;
      margin-top: 8px;
      padding-top: 10px;
      color: #0d6efd;
    }

    /* ─── Badge ─── */
    .badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 600;
    }

    .badge-success { background: #d1e7dd; color: #0f5132; }
    .badge-warning { background: #fff3cd; color: #664d03; }
    .badge-danger  { background: #f8d7da; color: #842029; }
    .badge-secondary { background: #e2e3e5; color: #41464b; }
    .badge-primary { background: #cfe2ff; color: #084298; }

    /* ─── Footer ─── */
    .doc-footer {
      margin-top: 32px;
      padding-top: 14px;
      border-top: 1px solid #dee2e6;
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #adb5bd;
    }

    /* ─── Signature ─── */
    .signature-area {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      margin-top: 40px;
    }

    .signature-box {
      text-align: center;
      padding-top: 48px;
      border-top: 1px dashed #adb5bd;
      font-size: 12px;
      color: #6c757d;
    }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { padding: 14mm 18mm; }
    }
  `

  // ─── 1. พิมพ์ข้อมูลลูกบ้าน ────────────────────────────
  const printResident = (resident, invoiceItems = [], paymentItems = []) => {
    const fmt = (v) => new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(v || 0)
    const fmtDate = (v) => v ? new Intl.DateTimeFormat('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(v)) : '-'
    const now = fmtDate(new Date())

    const statusLabel = { paid: 'ชำระแล้ว', partial: 'ชำระบางส่วน', overdue: 'ค้างชำระ', pending: 'รอชำระ' }
    const statusBadge = { paid: 'badge-success', partial: 'badge-warning', overdue: 'badge-danger', pending: 'badge-secondary' }
    const typeLabel = { owner: 'เจ้าของ', tenant: 'ผู้เช่า' }
    const occupancyLabel = { active: 'กำลังพักอาศัย', inactive: 'ย้ายออก' }

    const invoiceRows = invoiceItems.map((inv) => `
      <tr>
        <td>${inv.id}</td>
        <td>${inv.billingMonth}</td>
        <td class="text-end">${fmt(inv.amount)}</td>
        <td>${fmtDate(inv.dueDate)}</td>
        <td><span class="badge ${statusBadge[inv.status] || 'badge-secondary'}">${statusLabel[inv.status] || inv.status}</span></td>
      </tr>
    `).join('')

    const paymentRows = paymentItems.map((pay) => `
      <tr>
        <td>${pay.id}</td>
        <td>${fmtDate(pay.date)}</td>
        <td>${pay.method}</td>
        <td class="text-end">${fmt(pay.amount)}</td>
        <td>${pay.receiptNo}</td>
      </tr>
    `).join('')

    printHtml(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>ข้อมูลลูกบ้าน - ${resident.name}</title>
    <style>${basePrintCss}</style></head><body><div class="page">

      <div class="doc-header">
        <div class="doc-logo">Central Fee<small>Management System</small></div>
        <div class="doc-title">
          <h1>ข้อมูลลูกบ้าน</h1>
          <div class="doc-no">${resident.residentCode} | พิมพ์เมื่อ ${now}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">ข้อมูลส่วนตัว</div>
        <div class="grid-2">
          <div><div class="field-label">รหัสลูกบ้าน</div><div class="field-value">${resident.residentCode}</div></div>
          <div><div class="field-label">ชื่อ-นามสกุล</div><div class="field-value">${resident.name}</div></div>
          <div><div class="field-label">ประเภท</div><div class="field-value">${typeLabel[resident.residentType] || resident.residentType}</div></div>
          <div><div class="field-label">สถานะการพักอาศัย</div><div class="field-value">${occupancyLabel[resident.occupancyStatus] || resident.occupancyStatus}</div></div>
          <div><div class="field-label">วันที่เข้าอยู่</div><div class="field-value">${fmtDate(resident.moveInDate)}</div></div>
          <div><div class="field-label">ชำระล่าสุด</div><div class="field-value">${fmtDate(resident.lastPaymentDate)}</div></div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">ข้อมูลการติดต่อ</div>
        <div class="grid-2">
          <div><div class="field-label">เบอร์โทรศัพท์</div><div class="field-value">${resident.phone || '-'}</div></div>
          <div><div class="field-label">อีเมล</div><div class="field-value">${resident.email || '-'}</div></div>
          <div><div class="field-label">ที่อยู่</div><div class="field-value">${resident.address || '-'}</div></div>
          <div><div class="field-label">ยูนิต / อาคาร</div><div class="field-value">${resident.unitCode} / ${resident.building}</div></div>
          <div><div class="field-label">ผู้ติดต่อฉุกเฉิน</div><div class="field-value">${resident.emergencyContact || '-'}</div></div>
          <div><div class="field-label">เบอร์โทรฉุกเฉิน</div><div class="field-value">${resident.emergencyPhone || '-'}</div></div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">สถานะการเงิน</div>
        <div class="grid-3">
          <div>
            <div class="field-label">สถานะการชำระ</div>
            <div class="field-value">
              <span class="badge ${statusBadge[resident.paymentStatus] || 'badge-secondary'}">
                ${statusLabel[resident.paymentStatus] || resident.paymentStatus}
              </span>
            </div>
          </div>
          <div><div class="field-label">ยอดค้างชำระ</div><div class="field-value" style="color:#dc3545">${fmt(resident.outstanding)}</div></div>
          <div><div class="field-label">หมายเหตุ</div><div class="field-value">${resident.note || '-'}</div></div>
        </div>
      </div>

      ${invoiceItems.length ? `
      <div class="section">
        <div class="section-title">ประวัติใบแจ้งหนี้</div>
        <table><thead><tr>
          <th>เลขที่บิล</th><th>รอบบิล</th><th class="text-end">ยอดเงิน</th><th>ครบกำหนด</th><th>สถานะ</th>
        </tr></thead><tbody>${invoiceRows}</tbody></table>
      </div>` : ''}

      ${paymentItems.length ? `
      <div class="section">
        <div class="section-title">ประวัติการชำระเงิน</div>
        <table><thead><tr>
          <th>เลขที่รับชำระ</th><th>วันที่ชำระ</th><th>ช่องทาง</th><th class="text-end">จำนวนเงิน</th><th>ใบเสร็จ</th>
        </tr></thead><tbody>${paymentRows}</tbody></table>
      </div>` : ''}

      <div class="signature-area">
        <div class="signature-box">ผู้พักอาศัย / ลูกบ้าน</div>
        <div class="signature-box">เจ้าหน้าที่การเงิน</div>
      </div>

      <div class="doc-footer">
        <span>Central Fee Management System</span>
        <span>พิมพ์เมื่อ ${now}</span>
      </div>
    </div></body></html>`)
  }

  // ─── 2. พิมพ์ข้อมูลยูนิต ──────────────────────────────
  const printUnit = (unit, invoiceItems = [], paymentItems = []) => {
    const fmt = (v) => new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(v || 0)
    const fmtDate = (v) => v ? new Intl.DateTimeFormat('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(v)) : '-'
    const now = fmtDate(new Date())

    const typeLabel = { house: 'บ้าน', condo: 'ห้องชุด', shop: 'ร้านค้า' }
    const occupancyLabel = { occupied: 'มีผู้พักอาศัย', vacant: 'ว่าง' }
    const statusLabel = { paid: 'ชำระแล้ว', partial: 'ชำระบางส่วน', overdue: 'ค้างชำระ', pending: 'รอชำระ', vacant: 'ว่าง' }
    const statusBadge = { paid: 'badge-success', partial: 'badge-warning', overdue: 'badge-danger', pending: 'badge-secondary', vacant: 'badge-secondary' }

    const invoiceRows = invoiceItems.map((inv) => `
      <tr>
        <td>${inv.id}</td><td>${inv.billingMonth}</td>
        <td class="text-end">${fmt(inv.amount)}</td><td>${fmtDate(inv.dueDate)}</td>
        <td><span class="badge ${statusBadge[inv.status] || 'badge-secondary'}">${statusLabel[inv.status] || inv.status}</span></td>
      </tr>`).join('')

    const paymentRows = paymentItems.map((pay) => `
      <tr>
        <td>${pay.id}</td><td>${fmtDate(pay.date)}</td><td>${pay.method}</td>
        <td class="text-end">${fmt(pay.amount)}</td><td>${pay.receiptNo}</td>
      </tr>`).join('')

    printHtml(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>ข้อมูลยูนิต - ${unit.unitCode}</title>
    <style>${basePrintCss}</style></head><body><div class="page">

      <div class="doc-header">
        <div class="doc-logo">Central Fee<small>Management System</small></div>
        <div class="doc-title">
          <h1>ข้อมูลยูนิต</h1>
          <div class="doc-no">${unit.unitCode} | พิมพ์เมื่อ ${now}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">ข้อมูลยูนิต</div>
        <div class="grid-3">
          <div><div class="field-label">รหัสยูนิต</div><div class="field-value">${unit.unitCode}</div></div>
          <div><div class="field-label">บ้านเลขที่</div><div class="field-value">${unit.houseNo}</div></div>
          <div><div class="field-label">อาคาร</div><div class="field-value">${unit.building}</div></div>
          <div><div class="field-label">โซน</div><div class="field-value">${unit.zone || '-'}</div></div>
          <div><div class="field-label">ประเภท</div><div class="field-value">${typeLabel[unit.unitType] || unit.unitType}</div></div>
          <div><div class="field-label">พื้นที่</div><div class="field-value">${unit.area} ตร.ม.</div></div>
          <div><div class="field-label">ที่จอดรถ</div><div class="field-value">${unit.parkingSlots} คัน</div></div>
          <div><div class="field-label">ค่าส่วนกลาง/เดือน</div><div class="field-value">${fmt(unit.monthlyFee)}</div></div>
          <div><div class="field-label">สถานะ</div><div class="field-value">
            <span class="badge ${statusBadge[unit.paymentStatus] || 'badge-secondary'}">${statusLabel[unit.paymentStatus] || unit.paymentStatus}</span>
          </div></div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">ข้อมูลผู้พักอาศัย</div>
        <div class="grid-2">
          <div><div class="field-label">สถานะการอยู่อาศัย</div><div class="field-value">${occupancyLabel[unit.occupancyStatus] || unit.occupancyStatus}</div></div>
          <div><div class="field-label">ชื่อลูกบ้าน / เจ้าของ</div><div class="field-value">${unit.residentName || '-'}</div></div>
          <div><div class="field-label">เบอร์โทรศัพท์</div><div class="field-value">${unit.phone || '-'}</div></div>
          <div><div class="field-label">อีเมล</div><div class="field-value">${unit.email || '-'}</div></div>
          <div><div class="field-label">วันที่เข้าอยู่</div><div class="field-value">${fmtDate(unit.moveInDate)}</div></div>
          <div><div class="field-label">ชำระล่าสุด</div><div class="field-value">${fmtDate(unit.lastPaymentDate)}</div></div>
        </div>
      </div>

      <div class="summary-box" style="margin-bottom:20px">
        <div class="summary-row"><span>ค่าส่วนกลางต่อเดือน</span><span>${fmt(unit.monthlyFee)}</span></div>
        <div class="summary-row total"><span>ยอดค้างชำระรวม</span><span style="color:${unit.balance > 0 ? '#dc3545' : '#198754'}">${fmt(unit.balance)}</span></div>
      </div>

      ${unit.note ? `<div class="section"><div class="section-title">หมายเหตุ</div><p style="font-size:13px;color:#495057">${unit.note}</p></div>` : ''}

      ${invoiceItems.length ? `
      <div class="section">
        <div class="section-title">ประวัติใบแจ้งหนี้</div>
        <table><thead><tr>
          <th>เลขที่บิล</th><th>รอบบิล</th><th class="text-end">ยอดเงิน</th><th>ครบกำหนด</th><th>สถานะ</th>
        </tr></thead><tbody>${invoiceRows}</tbody></table>
      </div>` : ''}

      ${paymentItems.length ? `
      <div class="section">
        <div class="section-title">ประวัติการชำระเงิน</div>
        <table><thead><tr>
          <th>เลขที่รับชำระ</th><th>วันที่ชำระ</th><th>ช่องทาง</th><th class="text-end">จำนวนเงิน</th><th>ใบเสร็จ</th>
        </tr></thead><tbody>${paymentRows}</tbody></table>
      </div>` : ''}

      <div class="doc-footer">
        <span>Central Fee Management System</span>
        <span>พิมพ์เมื่อ ${now}</span>
      </div>
    </div></body></html>`)
  }

  // ─── 3. พิมพ์ใบแจ้งหนี้ ───────────────────────────────
  const printInvoice = (invoice, lineItems = [], paymentHistory = []) => {
    const fmt = (v) => new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(v || 0)
    const fmtDate = (v) => v ? new Intl.DateTimeFormat('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(v)) : '-'
    const now = fmtDate(new Date())

    const statusLabel = { paid: 'ชำระแล้ว', partial: 'ชำระบางส่วน', overdue: 'ค้างชำระ', pending: 'รอชำระ' }
    const statusBadge = { paid: 'badge-success', partial: 'badge-warning', overdue: 'badge-danger', pending: 'badge-secondary' }
    const balance = (invoice.totalAmount || 0) - (invoice.paidAmount || 0)

    const lineRows = lineItems.map((item) => `
      <tr>
        <td>${item.description}</td>
        <td class="text-center">${item.qty}</td>
        <td class="text-end">${fmt(item.unitPrice)}</td>
        <td class="text-end">${fmt(item.amount)}</td>
      </tr>`).join('')

    const payRows = paymentHistory.map((pay) => `
      <tr>
        <td>${pay.id}</td><td>${fmtDate(pay.date)}</td><td>${pay.method}</td>
        <td class="text-end" style="color:#198754;font-weight:600">${fmt(pay.amount)}</td>
        <td>${pay.receiptNo}</td><td>${pay.recordedBy || '-'}</td>
      </tr>`).join('')

    printHtml(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>ใบแจ้งหนี้ - ${invoice.invoiceNo}</title>
    <style>${basePrintCss}</style></head><body><div class="page">

      <div class="doc-header">
        <div class="doc-logo">Central Fee<small>Management System</small></div>
        <div class="doc-title">
          <h1>ใบแจ้งหนี้</h1>
          <div class="doc-no">${invoice.invoiceNo}</div>
          <div style="margin-top:6px">
            <span class="badge ${statusBadge[invoice.status] || 'badge-secondary'}">
              ${statusLabel[invoice.status] || invoice.status}
            </span>
          </div>
        </div>
      </div>

      <div class="grid-2" style="margin-bottom:20px">
        <div class="section">
          <div class="section-title">ข้อมูลผู้ชำระ / ยูนิต</div>
          <div><div class="field-label">ชื่อลูกบ้าน</div><div class="field-value">${invoice.residentName}</div></div>
          <div style="margin-top:8px"><div class="field-label">ยูนิต / อาคาร</div><div class="field-value">${invoice.unitCode} / ${invoice.building}</div></div>
        </div>
        <div class="section">
          <div class="section-title">รายละเอียดบิล</div>
          <div><div class="field-label">รอบบิล</div><div class="field-value">${invoice.billingMonth}</div></div>
          <div style="margin-top:8px"><div class="field-label">วันที่ออกบิล</div><div class="field-value">${fmtDate(invoice.issueDate)}</div></div>
          <div style="margin-top:8px"><div class="field-label">วันครบกำหนดชำระ</div><div class="field-value" style="color:#dc3545">${fmtDate(invoice.dueDate)}</div></div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">รายการเรียกเก็บ</div>
        <table>
          <thead><tr>
            <th>รายการ</th><th class="text-center">จำนวน</th><th class="text-end">ราคา/หน่วย</th><th class="text-end">รวม</th>
          </tr></thead>
          <tbody>${lineRows || `<tr><td colspan="4" class="text-center" style="color:#adb5bd;padding:16px">ไม่มีรายการ</td></tr>`}</tbody>
        </table>
      </div>

      <div class="summary-box">
        <div class="summary-row"><span>ค่าส่วนกลาง</span><span>${fmt(invoice.amount)}</span></div>
        ${invoice.penalty ? `<div class="summary-row"><span>ค่าปรับ</span><span style="color:#dc3545">${fmt(invoice.penalty)}</span></div>` : ''}
        <div class="summary-row"><span>ยอดที่ชำระแล้ว</span><span style="color:#198754">${fmt(invoice.paidAmount)}</span></div>
        <div class="summary-row total"><span>ยอดคงเหลือ</span><span style="color:${balance > 0 ? '#dc3545' : '#198754'}">${fmt(balance)}</span></div>
      </div>

      ${paymentHistory.length ? `
      <div class="section" style="margin-top:20px">
        <div class="section-title">ประวัติการรับชำระ</div>
        <table><thead><tr>
          <th>เลขที่รับชำระ</th><th>วันที่ชำระ</th><th>ช่องทาง</th><th class="text-end">จำนวนเงิน</th><th>ใบเสร็จ</th><th>ผู้บันทึก</th>
        </tr></thead><tbody>${payRows}</tbody></table>
      </div>` : ''}

      ${invoice.note ? `<div class="section" style="margin-top:16px"><div class="section-title">หมายเหตุ</div><p style="font-size:13px;color:#495057">${invoice.note}</p></div>` : ''}

      <div class="signature-area">
        <div class="signature-box">ผู้ชำระเงิน</div>
        <div class="signature-box">เจ้าหน้าที่การเงิน</div>
      </div>

      <div class="doc-footer">
        <span>Central Fee Management System</span>
        <span>พิมพ์เมื่อ ${now}</span>
      </div>
    </div></body></html>`)
  }

  // ─── 4. พิมพ์ใบเสร็จรับเงิน ───────────────────────────
  const printReceipt = (payment) => {
    const fmt = (v) => new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(v || 0)
    const fmtDate = (v) => v ? new Intl.DateTimeFormat('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(v)) : '-'
    const now = fmtDate(new Date())

    const methodLabel = { cash: 'เงินสด', bank_transfer: 'โอนธนาคาร', qr_payment: 'QR Payment', credit_card: 'บัตรเครดิต' }
    const statusLabel = { completed: 'รับชำระสมบูรณ์', partial: 'ชำระบางส่วน', pending_verify: 'รอตรวจสอบ' }
    const statusBadge = { completed: 'badge-success', partial: 'badge-warning', pending_verify: 'badge-secondary' }

    printHtml(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>ใบเสร็จรับเงิน - ${payment.receiptNo}</title>
    <style>${basePrintCss}
      .receipt-amount {
        text-align: center;
        background: #f0f7ff;
        border: 2px solid #cfe2ff;
        border-radius: 12px;
        padding: 20px;
        margin: 20px 0;
      }
      .receipt-amount .label { font-size: 13px; color: #6c757d; margin-bottom: 6px; }
      .receipt-amount .amount { font-size: 36px; font-weight: 700; color: #0d6efd; }
      .receipt-amount .baht { font-size: 14px; color: #6c757d; margin-top: 4px; }
    </style></head><body><div class="page">

      <div class="doc-header">
        <div class="doc-logo">Central Fee<small>Management System</small></div>
        <div class="doc-title">
          <h1>ใบเสร็จรับเงิน</h1>
          <div class="doc-no">${payment.receiptNo !== '-' ? payment.receiptNo : payment.paymentNo}</div>
          <div style="margin-top:6px">
            <span class="badge ${statusBadge[payment.status] || 'badge-secondary'}">
              ${statusLabel[payment.status] || payment.status}
            </span>
          </div>
        </div>
      </div>

      <div class="receipt-amount">
        <div class="label">จำนวนเงินที่รับชำระ</div>
        <div class="amount">${fmt(payment.amount)}</div>
        <div class="baht">บาทถ้วน</div>
      </div>

      <div class="grid-2" style="margin-bottom:20px">
        <div class="section">
          <div class="section-title">ข้อมูลผู้ชำระ</div>
          <div><div class="field-label">ชื่อลูกบ้าน</div><div class="field-value">${payment.residentName}</div></div>
          <div style="margin-top:8px"><div class="field-label">ยูนิต / อาคาร</div><div class="field-value">${payment.unitCode} / ${payment.building}</div></div>
          <div style="margin-top:8px"><div class="field-label">ใบแจ้งหนี้อ้างอิง</div><div class="field-value">${payment.invoiceNo}</div></div>
        </div>
        <div class="section">
          <div class="section-title">ข้อมูลการรับชำระ</div>
          <div><div class="field-label">เลขที่รับชำระ</div><div class="field-value">${payment.paymentNo}</div></div>
          <div style="margin-top:8px"><div class="field-label">วันที่รับชำระ</div><div class="field-value">${fmtDate(payment.paymentDate)}</div></div>
          <div style="margin-top:8px"><div class="field-label">ช่องทางชำระ</div><div class="field-value">${methodLabel[payment.method] || payment.method}</div></div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">รายละเอียดการชำระ</div>
        <div class="grid-2">
          <div><div class="field-label">ธนาคาร / ระบบ</div><div class="field-value">${payment.bankName !== '-' ? payment.bankName : '-'}</div></div>
          <div><div class="field-label">Reference No.</div><div class="field-value">${payment.referenceNo !== '-' ? payment.referenceNo : '-'}</div></div>
          <div><div class="field-label">ผู้บันทึก</div><div class="field-value">${payment.recordedBy}</div></div>
          <div><div class="field-label">ผู้ตรวจสอบ</div><div class="field-value">${payment.verifiedBy !== '-' ? payment.verifiedBy : '-'}</div></div>
        </div>
      </div>

      ${payment.note ? `<div class="section"><div class="section-title">หมายเหตุ</div><p style="font-size:13px;color:#495057">${payment.note}</p></div>` : ''}

      <div class="signature-area">
        <div class="signature-box">ผู้ชำระเงิน</div>
        <div class="signature-box">เจ้าหน้าที่การเงิน</div>
      </div>

      <div class="doc-footer">
        <span>Central Fee Management System</span>
        <span>พิมพ์เมื่อ ${now}</span>
      </div>
    </div></body></html>`)
  }

  return {
    printResident,
    printUnit,
    printInvoice,
    printReceipt,
  }
}